import { ShieldCheck, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import SectionHeading from '@/components/feature/SectionHeading'
import useCommunity from '@/hooks/useCommunity'
import { useLanguage } from '@/localization'

/**
 * Universal code-of-conduct cards for the MC home. The conduct rules are shared
 * across every Uliunai server (sourced from the platform `useCommunity().conduct`
 * data), grass-skinned with a numbered badge per card. Links out to the full
 * MC-specific /rules page for the rest.
 */
export default function ConductSection() {
  const { conduct } = useCommunity()
  const { t } = useLanguage()

  if (conduct.length === 0) return null

  // The home shows the headline pillars only; the full rulebook lives on the hub.
  const pillars = conduct.slice(0, 3)

  return (
    <section id="conduct" className="mx-auto w-full max-w-6xl px-4 py-16 sm:py-20">
      <SectionHeading
        eyebrow="House rules"
        title="How we play together"
        subtitle="A short, shared code of conduct that applies across every Uliunai server. Be decent, play fair, keep it fun."
      />

      <div className="grid gap-6 md:grid-cols-3">
        {pillars.map((rule, i) => (
          <div
            key={rule.title}
            className="relative flex flex-col rounded-2xl border border-grass-dark/30 bg-white p-7 shadow-block"
          >
            <span className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-grass-dark font-pixel text-base text-white shadow-block">
              {i + 1}
            </span>
            <h3 className="mb-2 text-xl font-extrabold text-slate-deep">{t(rule.title)}</h3>
            <p className="text-slate-deep/70">{t(rule.summary)}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex flex-col items-center gap-3 text-center">
        <a
          href="https://uliunai.com/conduct"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-deep/70 underline-offset-4 transition-colors hover:text-grass-dark hover:underline"
        >
          <ShieldCheck size={16} className="text-grass-dark" />
          {t('These apply across all Uliunai servers — read the full Code of Conduct')}
        </a>
        <Link
          to="/rules"
          className="inline-flex items-center gap-1.5 font-bold text-grass-deep underline-offset-4 transition-colors hover:text-grass-dark hover:underline"
        >
          {t('Read the full Minecraft rules')} <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  )
}
